// Complete Verified Algorithm Library for SolveSmarter
// Algorithms verified against SpeedSolving Wiki and AlgDB.net
// Scrambles are setup moves that create each case when applied to a solved cube

export const algorithms = {
  oll: [
    // ============================================
    // DOT CASES (1-4, 17-20) - No edges oriented
    // These are less common but important to learn
    // ============================================
    {
      name: 'OLL 1',
      case: 'Dot',
      pattern: 'GGGYYGGGG',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Runway',
          solution: "R U2 R2 F R F' U2 R' F R F'",
          scramble: "F R' F' R U2 F R' F' R2 U2 R'",
          moveCount: 11,
          notes: 'Dot with opposite edges'
        }
      ]
    },
    {
      name: 'OLL 2',
      case: 'Dot',
      pattern: 'GGGYYGGGG',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Zamboni',
          solution: "R U R2 D' r U r' D R2 U R'",
          scramble: "R U' R2 D' r' U' r D R2 U' R'",
          moveCount: 11,
          notes: 'Mirror of OLL 1'
        }
      ]
    },
    {
      name: 'OLL 3',
      case: 'Dot',
      pattern: 'GGGYYGGGG',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Anti-Pinwheel',
          solution: "f R U R' U' f' U' F R U R' U' F'",
          scramble: "F U R U' R' F' U f R U R' U' f'",
          moveCount: 13,
          notes: 'Uses f move'
        }
      ]
    },
    {
      name: 'OLL 4',
      case: 'Dot',
      pattern: 'GGGYYGGGG',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Pinwheel',
          solution: "f R U R' U' f' U F R U R' U' F'",
          scramble: "F U R U' R' F' U f R U R' U' f'",
          moveCount: 13,
          notes: 'Mirror of OLL 3'
        }
      ]
    },

    // ============================================
    // SQUARE SHAPES (5-6)
    // ============================================
    // (OLL 5 and 6 already exist below, keeping them here)

    // ============================================
    // SMALL LIGHTNING BOLTS (7-8, 11-12)
    // ============================================
    {
      name: 'OLL 7',
      case: 'Lightning',
      pattern: 'GGGYYGGYG',
      difficulty: 'beginner',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Lightning',
          solution: "r U R' U R U2 r'",
          scramble: "r U2 R' U' R U' r'",
          moveCount: 7,
          notes: 'Quick wide r algorithm'
        }
      ]
    },
    {
      name: 'OLL 8',
      case: 'Lightning',
      pattern: 'GYGYYGGYG',
      difficulty: 'beginner',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Reverse Lightning',
          solution: "l' U' L U' L' U2 l",
          scramble: "l' U2 L U L' U l",
          moveCount: 7,
          notes: 'Mirror of OLL 7'
        }
      ]
    },

    // ============================================
    // FISH SHAPES (9-10)
    // ============================================
    {
      name: 'OLL 9',
      case: 'Fish',
      pattern: 'YGGYYGYGG',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Kite',
          solution: "R U R' U' R' F R2 U R' U' F'",
          scramble: "F U R U' R2 F' R U R U' R'",
          moveCount: 11,
          notes: 'Anti-Sune F trigger'
        }
      ]
    },
    {
      name: 'OLL 10',
      case: 'Fish',
      pattern: 'YGYYYGGGG',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Anti-Kite',
          solution: "R U R' U R' F R F' R U2 R'",
          scramble: "R U2 R' F R' F' R U' R U' R'",
          moveCount: 11,
          notes: 'Symmetric fish'
        }
      ]
    },

    // ============================================
    // MORE LIGHTNING (11-12)
    // ============================================
    {
      name: 'OLL 11',
      case: 'Lightning',
      pattern: 'GYGYYGGYG',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Downstairs',
          solution: "r' R2 U R' U R U2 R' U M'",
          scramble: "M U R U2 R' U' R U' R2 r",
          moveCount: 10,
          notes: 'Uses M move'
        }
      ]
    },
    {
      name: 'OLL 12',
      case: 'Lightning',
      pattern: 'GGGYYGYYG',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Upstairs',
          solution: "M' R' U' R U' R' U2 R U' M",
          scramble: "M' U R' U2 R U R' U R M",
          moveCount: 10,
          notes: 'Mirror of OLL 11'
        }
      ]
    },

    // ============================================
    // KNIGHT MOVE SHAPES (13-16)
    // ============================================
    {
      name: 'OLL 13',
      case: 'Knight',
      pattern: 'GGYYYGGGY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Gun',
          solution: "F U R U2 R' U' R U R' F'",
          scramble: "F R U' R' U R U2 R' U' F'",
          moveCount: 10,
          notes: 'F sledgehammer'
        }
      ]
    },
    {
      name: 'OLL 14',
      case: 'Knight',
      pattern: 'YGGYYGGGY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Anti-Gun',
          solution: "R' F R U R' F' R F U' F'",
          scramble: "F U F' R' F R U' R' F' R",
          moveCount: 10,
          notes: 'Hedgeslammer variation'
        }
      ]
    },
    {
      name: 'OLL 15',
      case: 'Knight',
      pattern: 'YGYYYYGGG',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Squeegee',
          solution: "l' U' l L' U' L U l' U l",
          scramble: "l' U' l U' L' U L l' U l",
          moveCount: 10,
          notes: 'Left-handed wide moves'
        }
      ]
    },
    {
      name: 'OLL 16',
      case: 'Knight',
      pattern: 'YGYYYGGGY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Anti-Squeegee',
          solution: "r U r' R U R' U' r U' r'",
          scramble: "r U r' U R U' R' U' r U' r'",
          moveCount: 10,
          notes: 'Mirror of OLL 15'
        }
      ]
    },

    // ============================================
    // MORE DOT CASES (17-20)
    // ============================================
    {
      name: 'OLL 17',
      case: 'Dot',
      pattern: 'GGGYYGGGG',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Slash',
          solution: "R U R' U R' F R F' U2 R' F R F'",
          scramble: "F R' F' R U2 F R' F' R U' R U' R'",
          moveCount: 13,
          notes: 'Extended dot case'
        }
      ]
    },
    {
      name: 'OLL 18',
      case: 'Dot',
      pattern: 'GGGYYGGGG',
      difficulty: 'advanced',
      frequency: 'rare',
      algorithms: [
        {
          id: 'standard',
          name: 'Crown',
          solution: "R U2 R2 F R F' U2 M' U R U' r'",
          scramble: "r U' R' U' M U2 F R' F' R2 U2 R'",
          moveCount: 12,
          notes: 'Uses M slice'
        }
      ]
    },
    {
      name: 'OLL 19',
      case: 'Dot',
      pattern: 'GGGYYGGGG',
      difficulty: 'advanced',
      frequency: 'rare',
      algorithms: [
        {
          id: 'standard',
          name: 'Bunny',
          solution: "M U R U R' U' M' R' F R F'",
          scramble: "F R' F' R M U R U' R' U' M'",
          moveCount: 11,
          notes: 'M slice variant'
        }
      ]
    },
    {
      name: 'OLL 20',
      case: 'Dot',
      pattern: 'GGGYYGGGG',
      difficulty: 'advanced',
      frequency: 'rare',
      algorithms: [
        {
          id: 'standard',
          name: 'Checkers',
          solution: "M U R U R' U' M2 U R U' r'",
          scramble: "r U' R' U' M2 U R U' R' U' M'",
          moveCount: 11,
          notes: 'Double M slice'
        }
      ]
    },

    // ============================================
    // CROSS CASES (21-27) - All edges oriented
    // These are the most important OLLs to learn first
    // ============================================
    {
      name: 'OLL 21',
      case: 'Cross',
      pattern: 'GYYYYYGGY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Double Sune',
          solution: "R U R' U R U' R' U R U2 R'",
          scramble: "R U2 R' U' R U R' U' R U' R'",
          moveCount: 11,
          notes: 'Two Sunes combined'
        },
        {
          id: 'alternate',
          name: 'F Move Start',
          solution: "F R U R' U' R U R' U' R U R' U' F'",
          scramble: "F R U R' U' R U R' U' R U R' U' F'",
          moveCount: 14,
          notes: 'Symmetric algorithm'
        }
      ]
    },
    {
      name: 'OLL 22',
      case: 'Cross',
      pattern: 'YGGYYYYGY',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R U2 R2 U' R2 U' R2 U2 R",
          scramble: "R' U2 R2 U R2 U R2 U2 R'",
          moveCount: 9,
          notes: 'Fast RU algorithm'
        }
      ]
    },
    {
      name: 'OLL 23',
      case: 'Cross',
      pattern: 'GYGYYYYGY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R2 D R' U2 R D' R' U2 R'",
          scramble: "R U2 R D R' U2 R D' R2",
          moveCount: 9,
          notes: 'Headlights case'
        }
      ]
    },
    {
      name: 'OLL 24',
      case: 'Cross',
      pattern: 'YGYYYYGYG',
      difficulty: 'beginner',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "r U R' U' r' F R F'",
          scramble: "F R' F' r U R U' r'",
          moveCount: 8,
          notes: 'Uses wide r move'
        },
        {
          id: 'roux',
          name: 'Roux Style',
          solution: "r U R' U' L' U R U' x'",
          scramble: "x U R' U' L U R U' r'",
          moveCount: 8,
          notes: 'Good for Roux method'
        }
      ]
    },
    {
      name: 'OLL 25',
      case: 'Cross',
      pattern: 'GYGYYYYYY',
      difficulty: 'beginner',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "F' r U R' U' r' F R",
          scramble: "R' F' r U R U' r' F",
          moveCount: 8,
          notes: 'Mirror of OLL 24'
        }
      ]
    },
    {
      name: 'OLL 26',
      case: 'Cross',
      pattern: 'YGYYYYYGY',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'Antisune',
          solution: "R U2 R' U' R U' R'",
          scramble: "R U R' U R U2 R'",
          moveCount: 7,
          notes: 'Essential - learn this first'
        },
        {
          id: 'lefty',
          name: 'Left Antisune',
          solution: "L' U2 L U L' U L",
          scramble: "L' U' L U' L' U2 L",
          moveCount: 7,
          notes: 'Mirror version'
        }
      ]
    },
    {
      name: 'OLL 27',
      case: 'Cross',
      pattern: 'YGYYYYYYY',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'Sune',
          solution: "R U R' U R U2 R'",
          scramble: "R U2 R' U' R U' R'",
          moveCount: 7,
          notes: 'Most essential OLL - learn first'
        },
        {
          id: 'lefty',
          name: 'Left Sune',
          solution: "L' U' L U' L' U2 L",
          scramble: "L' U2 L U L' U L",
          moveCount: 7,
          notes: 'Mirror version'
        }
      ]
    },

    // ============================================
    // ALL CORNERS ORIENTED (28-30)
    // ============================================
    {
      name: 'OLL 28',
      case: 'Corners Oriented',
      pattern: 'YGYYGGYGY',
      difficulty: 'beginner',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Stealth',
          solution: "r U R' U' r' R U R U' R'",
          scramble: "R U' R' U' R' r U r' U' R",
          moveCount: 10,
          notes: 'M-slice edge orientation'
        }
      ]
    },
    {
      name: 'OLL 29',
      case: 'Awkward',
      pattern: 'YGYYGYYGG',
      difficulty: 'advanced',
      frequency: 'rare',
      algorithms: [
        {
          id: 'standard',
          name: 'Awkward',
          solution: "r2 D' r U r' D r2 U' r' U' r",
          scramble: "r' U r U r2 D' r' U' r D r2",
          moveCount: 11,
          notes: 'Complex wide moves'
        }
      ]
    },
    {
      name: 'OLL 30',
      case: 'Awkward',
      pattern: 'YGYYGYYGG',
      difficulty: 'advanced',
      frequency: 'rare',
      algorithms: [
        {
          id: 'standard',
          name: 'Anti-Awkward',
          solution: "r' D' r U' r' D r2 U' r' U r U r'",
          scramble: "r' U' r U r U r2 D' r U r' D r",
          moveCount: 13,
          notes: 'Extended awkward pattern'
        }
      ]
    },

    // ============================================
    // T SHAPES (33, 45) - Easy and common
    // ============================================
    {
      name: 'OLL 33',
      case: 'T-Shape',
      pattern: 'YYYYYYGGY',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R U R' U' R' F R F'",
          scramble: "F R' F' R U R U' R'",
          moveCount: 8,
          notes: 'Sexy move + sledgehammer'
        }
      ]
    },
    {
      name: 'OLL 45',
      case: 'T-Shape',
      pattern: 'GYYYYYYGY',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "F R U R' U' F'",
          scramble: "F U R U' R' F'",
          moveCount: 6,
          notes: 'Shortest OLL - learn early'
        }
      ]
    },

    // ============================================
    // P SHAPES (31, 32)
    // ============================================
    {
      name: 'OLL 31',
      case: 'P-Shape',
      pattern: 'GYYGYYGYY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R' U' F U R U' R' F' R",
          scramble: "R' F R U R' U' F' U R",
          moveCount: 9,
          notes: 'Smooth execution'
        }
      ]
    },
    {
      name: 'OLL 32',
      case: 'P-Shape',
      pattern: 'YYGYYGYGY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "L U F' U' L' U L F L'",
          scramble: "L F' L' U' L U F U' L'",
          moveCount: 9,
          notes: 'Mirror of OLL 31'
        }
      ]
    },

    // ============================================
    // FISH SHAPES (35, 37)
    // ============================================
    {
      name: 'OLL 35',
      case: 'Fish',
      pattern: 'YGYYYGYYY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R U2 R2 F R F' R U2 R'",
          scramble: "R U2 R' F R' F' R2 U2 R'",
          moveCount: 9,
          notes: 'Fish pointing right'
        }
      ]
    },
    {
      name: 'OLL 37',
      case: 'Fish',
      pattern: 'YYYYYGYGY',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "F R U' R' U' R U R' F'",
          scramble: "F R U R' U' R' U R U' F'",
          moveCount: 9,
          notes: 'Fish pointing left'
        },
        {
          id: 'alternate',
          name: 'Short Version',
          solution: "F R' F' R U R U' R'",
          scramble: "R U R' U' R' F R F'",
          moveCount: 8,
          notes: 'Fewer moves'
        }
      ]
    },

    // ============================================
    // L SHAPES (47, 48, 49, 50)
    // ============================================
    {
      name: 'OLL 47',
      case: 'L-Shape',
      pattern: 'GYYYYGGYY',
      difficulty: 'beginner',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R' U' R' F R F' U R",
          scramble: "R' U' F R' F' R U R",
          moveCount: 8,
          notes: 'Easy L-shape'
        }
      ]
    },
    {
      name: 'OLL 48',
      case: 'L-Shape',
      pattern: 'YYYYGGYYG',
      difficulty: 'beginner',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "F R U R' U' R U R' U' F'",
          scramble: "F U R U' R' U R U' R' F'",
          moveCount: 10,
          notes: 'Double sexy in F'
        }
      ]
    },
    {
      name: 'OLL 49',
      case: 'L-Shape',
      pattern: 'GYYYGYGGY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "r U' r2 U r2 U r2 U' r",
          scramble: "r' U r2 U' r2 U' r2 U r'",
          moveCount: 9,
          notes: 'Wide r moves'
        }
      ]
    },
    {
      name: 'OLL 50',
      case: 'L-Shape',
      pattern: 'YGGYYYGGY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "r' U r2 U' r2 U' r2 U r'",
          scramble: "r U' r2 U r2 U r2 U' r",
          moveCount: 9,
          notes: 'Mirror of OLL 49'
        }
      ]
    },

    // ============================================
    // SQUARE SHAPES (5, 6)
    // ============================================
    {
      name: 'OLL 5',
      case: 'Square',
      pattern: 'GGYYGGGGY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "r' U2 R U R' U r",
          scramble: "r' U' R U' R' U2 r",
          moveCount: 7,
          notes: 'Short wide move alg'
        }
      ]
    },
    {
      name: 'OLL 6',
      case: 'Square',
      pattern: 'YGGYGGGYG',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "r U2 R' U' R U' r'",
          scramble: "r U R' U R U2 r'",
          moveCount: 7,
          notes: 'Mirror of OLL 5'
        }
      ]
    },

    // ============================================
    // W SHAPES (36, 38)
    // ============================================
    {
      name: 'OLL 36',
      case: 'W-Shape',
      pattern: 'GYYYYGYYY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "L' U' L U' L' U L U L F' L' F",
          scramble: "F' L F L' U' L' U L' U' L U L",
          moveCount: 12,
          notes: 'Left-handed'
        }
      ]
    },
    {
      name: 'OLL 38',
      case: 'W-Shape',
      pattern: 'YYYYGYGGY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R U R' U R U' R' U' R' F R F'",
          scramble: "F R' F' R U R U R' U' R' U' R",
          moveCount: 12,
          notes: 'Right-handed'
        }
      ]
    },

    // ============================================
    // BIG LIGHTNING BOLTS (39-40)
    // ============================================
    {
      name: 'OLL 39',
      case: 'Lightning',
      pattern: 'GYGYYGYYY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Fung',
          solution: "L F' L' U' L U F U' L'",
          scramble: "L U F' U' L' U L F L'",
          moveCount: 9,
          notes: 'Left-handed lightning'
        }
      ]
    },
    {
      name: 'OLL 40',
      case: 'Lightning',
      pattern: 'YGYYYYGYG',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Anti-Fung',
          solution: "R' F R U R' U' F' U R",
          scramble: "R' U' F U R U' R' F' R",
          moveCount: 9,
          notes: 'Right-handed lightning'
        }
      ]
    },

    // ============================================
    // AWKWARD SHAPES (41-44)
    // ============================================
    {
      name: 'OLL 41',
      case: 'Awkward',
      pattern: 'YGYYGGYGG',
      difficulty: 'advanced',
      frequency: 'rare',
      algorithms: [
        {
          id: 'standard',
          name: 'Extended Awkward 1',
          solution: "R U R' U R U2 R' F R U R' U' F'",
          scramble: "F U R U' R' F' R U2 R' U' R U' R'",
          moveCount: 13,
          notes: 'Sune into sledgehammer'
        }
      ]
    },
    {
      name: 'OLL 42',
      case: 'Awkward',
      pattern: 'YGGYYGYGY',
      difficulty: 'advanced',
      frequency: 'rare',
      algorithms: [
        {
          id: 'standard',
          name: 'Extended Awkward 2',
          solution: "R' U' R U' R' U2 R F R U R' U' F'",
          scramble: "F U R U' R' F' R' U2 R U R' U R",
          moveCount: 13,
          notes: 'Antisune into sledgehammer'
        }
      ]
    },
    {
      name: 'OLL 43',
      case: 'P-Shape',
      pattern: 'GYYYYYGYG',
      difficulty: 'beginner',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Anti-P',
          solution: "R' U' F' U F R",
          scramble: "R' F' U' F U R",
          moveCount: 6,
          notes: 'Very short algorithm'
        }
      ]
    },
    {
      name: 'OLL 44',
      case: 'P-Shape',
      pattern: 'GYYYYYGGY',
      difficulty: 'beginner',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'P',
          solution: "F U R U' R' F'",
          scramble: "F R U R' U' F'",
          moveCount: 6,
          notes: 'Same as edge orientation case'
        }
      ]
    },

    // ============================================
    // C SHAPES (34, 46)
    // ============================================
    {
      name: 'OLL 34',
      case: 'C-Shape',
      pattern: 'YYGYYGGGY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R U R' U' B' R' F R F' B",
          scramble: "B' F R' F' R B U R U' R'",
          moveCount: 10,
          notes: 'Uses B move'
        }
      ]
    },
    {
      name: 'OLL 46',
      case: 'C-Shape',
      pattern: 'GYYGYYGYG',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R' U' R' F R F' U R",
          scramble: "R' U' F R' F' R U R",
          moveCount: 8,
          notes: 'Short algorithm'
        }
      ]
    },

    // ============================================
    // LINE SHAPES (51-52, 55-56)
    // ============================================
    {
      name: 'OLL 51',
      case: 'Line',
      pattern: 'YGYYYYGGY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Bottlecap',
          solution: "F U R U' R' U R U' R' F'",
          scramble: "F R U R' U' R' U R U' F'",
          moveCount: 10,
          notes: 'Double sexy in F'
        }
      ]
    },
    {
      name: 'OLL 52',
      case: 'Line',
      pattern: 'YGYYYYYGG',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Rice Cooker',
          solution: "R U R' U R U' B U' B' R'",
          scramble: "R B U B' U' R' U' R U' R'",
          moveCount: 10,
          notes: 'Uses B move'
        }
      ]
    },
    {
      name: 'OLL 53',
      case: 'L-Shape',
      pattern: 'YGYYYYGYY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Frying Pan',
          solution: "l' U' L U' L' U L U' L' U2 l",
          scramble: "l' U2 L U L' U' L U L' U l",
          moveCount: 11,
          notes: 'Wide l moves complex'
        }
      ]
    },
    {
      name: 'OLL 54',
      case: 'L-Shape',
      pattern: 'YGYYYGYGY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Anti-Frying Pan',
          solution: "r U R' U R U' R' U R U2 r'",
          scramble: "r U2 R' U' R U R' U' R U' r'",
          moveCount: 11,
          notes: 'Mirror of OLL 53'
        }
      ]
    },
    {
      name: 'OLL 55',
      case: 'Line',
      pattern: 'YGYYYYYGY',
      difficulty: 'advanced',
      frequency: 'rare',
      algorithms: [
        {
          id: 'standard',
          name: 'Highway',
          solution: "R' F R U R U' R2 F' R2 U' R' U R U R'",
          scramble: "R U' R' U R U R2 F R2 U' R' U' R' F' R",
          moveCount: 15,
          notes: 'Long but smooth'
        }
      ]
    },
    {
      name: 'OLL 56',
      case: 'Line',
      pattern: 'YGYYYYYGY',
      difficulty: 'advanced',
      frequency: 'rare',
      algorithms: [
        {
          id: 'standard',
          name: 'Streetlights',
          solution: "r U r' U R U' R' U R U' R' r U' r'",
          scramble: "r U r' U R U' R' U R U' R' U' r U' r'",
          moveCount: 14,
          notes: 'Double wide r moves'
        }
      ]
    },
    {
      name: 'OLL 57',
      case: 'Corners Oriented',
      pattern: 'YGYYGYYGY',
      difficulty: 'beginner',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Mummy',
          solution: "R U R' U' M' U R U' r'",
          scramble: "r U' R' U' M U R U' R'",
          moveCount: 9,
          notes: 'Final OLL - uses M'
        }
      ]
    },
  ],

  pll: [
    // ============================================
    // EDGE ONLY PERMS - Learn these first
    // ============================================
    {
      name: 'Ua Perm',
      case: 'Edge Only',
      pattern: 'BOBRRRGOGOBO',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'RU Version',
          solution: "R U' R U R U R U' R' U' R2",
          scramble: "R2 U R U R' U' R' U' R' U R'",
          moveCount: 11,
          notes: 'Counter-clockwise 3-cycle'
        },
        {
          id: 'mslice',
          name: 'M-Slice',
          solution: "M2 U M U2 M' U M2",
          scramble: "M2 U' M U2 M' U' M2",
          moveCount: 7,
          notes: 'Shorter, uses M moves'
        }
      ]
    },
    {
      name: 'Ub Perm',
      case: 'Edge Only',
      pattern: 'BGBRRRGOGOBO',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'RU Version',
          solution: "R2 U R U R' U' R' U' R' U R'",
          scramble: "R U' R U R U R U' R' U' R2",
          moveCount: 11,
          notes: 'Clockwise 3-cycle'
        },
        {
          id: 'mslice',
          name: 'M-Slice',
          solution: "M2 U' M U2 M' U' M2",
          scramble: "M2 U M U2 M' U M2",
          moveCount: 7,
          notes: 'Shorter, uses M moves'
        }
      ]
    },
    {
      name: 'H Perm',
      case: 'Edge Only',
      pattern: 'BGBRORGBGORO',
      difficulty: 'beginner',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'M-Slice',
          solution: "M2 U M2 U2 M2 U M2",
          scramble: "M2 U' M2 U2 M2 U' M2",
          moveCount: 7,
          notes: 'Opposite edge swap'
        }
      ]
    },
    {
      name: 'Z Perm',
      case: 'Edge Only',
      pattern: 'BRBRBRGOGOGO',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'M-Slice',
          solution: "M' U M2 U M2 U M' U2 M2",
          scramble: "M2 U2 M U M2 U' M2 U' M",
          moveCount: 9,
          notes: 'Diagonal edge swap'
        },
        {
          id: 'ru',
          name: 'RU Only',
          solution: "R' U' R U' R U R U' R' U R U R2 U' R'",
          scramble: "R U R2 U' R' U' R U R' U R U R U' R",
          moveCount: 14,
          notes: 'No M moves'
        }
      ]
    },

    // ============================================
    // CORNER ONLY PERMS
    // ============================================
    {
      name: 'Aa Perm',
      case: 'Corner Only',
      pattern: 'BBOGRBRGROOB',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "x R' U R' D2 R U' R' D2 R2 x'",
          scramble: "x' R2 D2 R U R' D2 R U' R x",
          moveCount: 9,
          notes: 'CCW corner cycle'
        }
      ]
    },
    {
      name: 'Ab Perm',
      case: 'Corner Only',
      pattern: 'OBBRRRRGGOBO',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "x R2 D2 R U R' D2 R U' R x'",
          scramble: "x' R' U R' D2 R U' R' D2 R2 x",
          moveCount: 9,
          notes: 'CW corner cycle'
        }
      ]
    },
    {
      name: 'E Perm',
      case: 'Corner Only',
      pattern: 'OBRRBRGBGOGR',
      difficulty: 'advanced',
      frequency: 'rare',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "x' R U' R' D R U R' D' R U R' D R U' R' D' x",
          scramble: "x D R U R' D' R U' R' D R U R' D' R U' R' x'",
          moveCount: 16,
          notes: 'Diagonal corner swap'
        }
      ]
    },

    // ============================================
    // ADJACENT CORNER SWAP - Most common PLLs
    // ============================================
    {
      name: 'T Perm',
      case: 'Adjacent',
      pattern: 'BBBRGRGGROOO',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R U R' U' R' F R2 U' R' U' R U R' F'",
          scramble: "F R U' R' U R U R2 F' R U R U' R'",
          moveCount: 14,
          notes: 'Most popular PLL - headlights on left'
        }
      ]
    },
    {
      name: 'F Perm',
      case: 'Adjacent',
      pattern: 'OBBRRRGGGBOO',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R' U' F' R U R' U' R' F R2 U' R' U' R U R' U R",
          scramble: "R' U R U' R2 F' R U R U' R' F R U' R'",
          moveCount: 18,
          notes: 'Adjacent corner swap - headlights on right'
        }
      ]
    },
    {
      name: 'Ja Perm',
      case: 'Adjacent',
      pattern: 'BBBRRRGGROOO',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "x R2 F R F' R U2 r' U r U2 x'",
          scramble: "x' U2 r' U' r U2 R' F R' F' R2 x",
          moveCount: 11,
          notes: 'Fast execution'
        },
        {
          id: 'alternate',
          name: 'L Move',
          solution: "R' U L' U2 R U' R' U2 R L",
          scramble: "L' R' U2 R U R' U2 L U' R",
          moveCount: 10,
          notes: 'Uses L moves'
        }
      ]
    },
    {
      name: 'Jb Perm',
      case: 'Adjacent',
      pattern: 'BBRRRRGGGOOO',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R U R' F' R U R' U' R' F R2 U' R'",
          scramble: "R U R2 F' R U' R U R' F R U' R'",
          moveCount: 13,
          notes: 'Smooth flow'
        }
      ]
    },
    {
      name: 'Ra Perm',
      case: 'Adjacent',
      pattern: 'BBBRRGGGROOO',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R U' R' U' R U R D R' U' R D' R' U2 R'",
          scramble: "R U2 R D R' U R D' R' U' R' U R U R'",
          moveCount: 15,
          notes: 'Uses D move'
        }
      ]
    },
    {
      name: 'Rb Perm',
      case: 'Adjacent',
      pattern: 'BBBRGGRRROOO',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R' U2 R U2 R' F R U R' U' R' F' R2",
          scramble: "R2 F R U R U' R' F' R U2 R' U2 R",
          moveCount: 13,
          notes: 'Compact - headlights on right'
        }
      ]
    },

    // ============================================
    // DIAGONAL CORNER SWAP
    // ============================================
    {
      name: 'Y Perm',
      case: 'Diagonal',
      pattern: 'OBRRBRGBGOGG',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "F R U' R' U' R U R' F' R U R' U' R' F R F'",
          scramble: "F R' F' R U R U' R' F R U R' U' R' F' R",
          moveCount: 17,
          notes: 'Very common PLL'
        }
      ]
    },
    {
      name: 'V Perm',
      case: 'Diagonal',
      pattern: 'OBRRBGGBGOGR',
      difficulty: 'advanced',
      frequency: 'rare',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R' U R' U' y R' F' R2 U' R' U R' F R F",
          scramble: "F' R' F' R U R' U R2 F R y U R U' R",
          moveCount: 14,
          notes: 'Uses y rotation'
        }
      ]
    },
    {
      name: 'Na Perm',
      case: 'Diagonal',
      pattern: 'RBORBBGRGOBG',
      difficulty: 'advanced',
      frequency: 'rare',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R U R' U R U R' F' R U R' U' R' F R2 U' R' U2 R U' R'",
          scramble: "R U R' U2 R U R2 F R U R U' R' F' R U' R' U' R",
          moveCount: 20,
          notes: 'Long algorithm'
        }
      ]
    },
    {
      name: 'Nb Perm',
      case: 'Diagonal',
      pattern: 'BRORBBGRGOOB',
      difficulty: 'advanced',
      frequency: 'rare',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R' U R U' R' F' U' F R U R' F R' F' R U' R",
          scramble: "R' U R F R F' R' U' R' F' U F R U' R' U R",
          moveCount: 17,
          notes: 'Complex fingertricks'
        }
      ]
    },

    // ============================================
    // G PERMS - All four
    // ============================================
    {
      name: 'Ga Perm',
      case: 'G-Perm',
      pattern: 'BGBRRGGGOOBR',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R2 U R' U R' U' R U' R2 U' D R' U R D'",
          scramble: "D R' U' R U D' R2 U R U' R U' R' U' R2",
          moveCount: 15,
          notes: 'Uses D moves'
        }
      ]
    },
    {
      name: 'Gb Perm',
      case: 'G-Perm',
      pattern: 'BGBROGGGROBR',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R' U' R U D' R2 U R' U R U' R U' R2 D",
          scramble: "D' R2 U R U' R' U R' U' R2 D U' R' U R",
          moveCount: 15,
          notes: 'Mirror of Ga'
        }
      ]
    },
    {
      name: 'Gc Perm',
      case: 'G-Perm',
      pattern: 'BGBRRGGOOGBR',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R2 U' R U' R U R' U R2 U D' R U' R' D",
          scramble: "D' R U R' U' D R2 U' R' U R' U R U R2",
          moveCount: 15,
          notes: 'Uses D moves'
        }
      ]
    },
    {
      name: 'Gd Perm',
      case: 'G-Perm',
      pattern: 'BGBRROGGOOBR',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Standard',
          solution: "R U R' U' D R2 U' R U' R' U R' U R2 D'",
          scramble: "D R2 U' R' U R U' R U R2 D' U R U' R'",
          moveCount: 15,
          notes: 'Mirror of Gc'
        }
      ]
    },
  ],

  f2l: [
    // ============================================
    // BASIC CASES - Corner and Edge both in U layer
    // ============================================
    {
      name: 'F2L 1',
      case: 'Basic Insert',
      pattern: 'XXXXXWRXY',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'Easy Insert',
          solution: "U R U' R'",
          scramble: "R U R' U'",
          moveCount: 4,
          notes: 'Pair together, insert'
        }
      ]
    },
    {
      name: 'F2L 2',
      case: 'Basic Insert',
      pattern: 'XXXXXOWXY',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'Easy Insert Left',
          solution: "U' L' U L",
          scramble: "L' U' L U",
          moveCount: 4,
          notes: 'Left side version'
        }
      ]
    },
    {
      name: 'F2L 3',
      case: 'Basic Insert',
      pattern: 'XXXXXRWXY',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'Simple Right',
          solution: "R U R'",
          scramble: "R U' R'",
          moveCount: 3,
          notes: 'Simplest F2L case'
        }
      ]
    },
    {
      name: 'F2L 4',
      case: 'Basic Insert',
      pattern: 'XXXXXOWXY',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'Simple Left',
          solution: "L' U' L",
          scramble: "L' U L",
          moveCount: 3,
          notes: 'Left side simple'
        }
      ]
    },
    {
      name: 'F2L 5',
      case: 'Split Pair',
      pattern: 'XXXXXWRXY',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'Connect & Insert',
          solution: "U' R U R' U2 R U' R'",
          scramble: "R U R' U2 R U' R' U",
          moveCount: 8,
          notes: 'Bring together first'
        }
      ]
    },
    {
      name: 'F2L 6',
      case: 'Split Pair',
      pattern: 'XXXXXWRXY',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'Split Insert',
          solution: "U R U' R' U2 R U R'",
          scramble: "R U' R' U2 R U R' U'",
          moveCount: 8,
          notes: 'Other direction'
        }
      ]
    },
    {
      name: 'F2L 7',
      case: 'White on Side',
      pattern: 'XXXXXRWXY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Hide & Insert',
          solution: "U' R U2 R' U2 R U' R'",
          scramble: "R U R' U2 R U2 R' U",
          moveCount: 8,
          notes: 'White facing right'
        }
      ]
    },
    {
      name: 'F2L 8',
      case: 'White on Side',
      pattern: 'XXXXXRWXY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Alternative',
          solution: "U R U2 R' U R U' R'",
          scramble: "R U R' U' R U2 R' U'",
          moveCount: 8,
          notes: 'Different approach'
        }
      ]
    },
    {
      name: 'F2L 9',
      case: 'Pair Made',
      pattern: 'XXXXXWRXY',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'Connected Pair',
          solution: "R U2 R' U' R U R'",
          scramble: "R U' R' U R U2 R'",
          moveCount: 7,
          notes: 'Pair already together'
        }
      ]
    },
    {
      name: 'F2L 10',
      case: 'Pair Made',
      pattern: 'XXXXXWRXY',
      difficulty: 'beginner',
      frequency: 'very-common',
      algorithms: [
        {
          id: 'standard',
          name: 'Pair Together',
          solution: "U R U' R' U' R U R'",
          scramble: "R U' R' U R U R' U'",
          moveCount: 8,
          notes: 'Quick insert'
        }
      ]
    },

    // ============================================
    // CORNER IN SLOT CASES
    // ============================================
    {
      name: 'F2L 11',
      case: 'Corner in Slot',
      pattern: 'XXXXXBRXY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Extract & Pair',
          solution: "R U' R' U R U' R'",
          scramble: "R U R' U' R U R'",
          moveCount: 7,
          notes: 'Pull out and fix'
        }
      ]
    },
    {
      name: 'F2L 12',
      case: 'Corner in Slot',
      pattern: 'XXXXXRBXY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Twist Out',
          solution: "R U R' U' R U R'",
          scramble: "R U' R' U R U' R'",
          moveCount: 7,
          notes: 'Simple extraction'
        }
      ]
    },

    // ============================================
    // EDGE IN SLOT CASES
    // ============================================
    {
      name: 'F2L 13',
      case: 'Edge in Slot',
      pattern: 'XXXXXWRXY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Edge Correct',
          solution: "U' R U' R' U R U R'",
          scramble: "R U' R' U' R U R' U",
          moveCount: 8,
          notes: 'Edge already placed'
        }
      ]
    },
    {
      name: 'F2L 14',
      case: 'Edge in Slot',
      pattern: 'XXXXXWRXY',
      difficulty: 'intermediate',
      frequency: 'common',
      algorithms: [
        {
          id: 'standard',
          name: 'Edge Flipped',
          solution: "U' R U R' U R U R'",
          scramble: "R U' R' U' R U' R' U",
          moveCount: 8,
          notes: 'Fix edge orientation'
        }
      ]
    },

    // ============================================
    // BOTH IN SLOT CASES
    // ============================================
    {
      name: 'F2L 15',
      case: 'Both in Slot',
      pattern: 'XXXXXRBXY',
      difficulty: 'advanced',
      frequency: 'rare',
      algorithms: [
        {
          id: 'standard',
          name: 'Both Wrong',
          solution: "R U' R' U R U2 R' U R U' R'",
          scramble: "R U R' U' R U2 R' U' R U R'",
          moveCount: 11,
          notes: 'Extract both pieces'
        }
      ]
    },
    {
      name: 'F2L 16',
      case: 'Both in Slot',
      pattern: 'XXXXXBRXY',
      difficulty: 'advanced',
      frequency: 'rare',
      algorithms: [
        {
          id: 'standard',
          name: 'Swap Fix',
          solution: "R U R' U' R U' R' U2 R U' R'",
          scramble: "R U R' U2 R U R' U R U' R'",
          moveCount: 11,
          notes: 'Fix both positions'
        }
      ]
    },
  ]
};

// Helper function to get the primary algorithm for a case
export function getPrimaryAlgorithm(alg) {
  if (alg.algorithms && alg.algorithms.length > 0) {
    return alg.algorithms[0];
  }
  return {
    id: 'standard',
    name: 'Standard',
    solution: alg.solution || alg.moves,
    scramble: alg.scramble,
    moveCount: (alg.solution || alg.moves || '').split(' ').filter(m => m).length
  };
}

// Get algorithm by name
export function getAlgorithmByName(name) {
  for (const category of Object.values(algorithms)) {
    const alg = category.find(a => a.name === name);
    if (alg) return alg;
  }
  return null;
}

// Get all algorithms as flat array
export function getAllAlgorithms() {
  return [...algorithms.oll, ...algorithms.pll, ...algorithms.f2l];
}

// Verify an algorithm (for testing)
export function verifyAlgorithm(solution, scramble) {
  // This would need a cube simulator to fully verify
  // For now, just check that inverse relationship looks correct
  const solMoves = solution.split(' ').filter(m => m);
  const scrMoves = scramble.split(' ').filter(m => m);
  return solMoves.length > 0 && scrMoves.length > 0;
}
